// First-party page-view beacon. Tracks the current page and time spent on it,
// batches events, and POSTs them to the /PageViews ingest endpoint. Uses
// fetch({ keepalive: true }) so queued events still send as the tab closes
// while carrying the Authorization header (navigator.sendBeacon cannot set it;
// the CORS preflight is cached after the first send, so unload flushes work).

// --- Route normalization -------------------------------------------------
// Collapse record ids to ':id' so detail pages group together, then map the
// normalized pattern (or its first segment) to a friendly label.
const LABELS = {
  '/': 'Home',
  '/dashboard': 'Dashboard',
  '/notes': 'Activity Notes',
  '/notes/:id': 'Activity Note Detail',
  '/notes_report': 'Activity Notes Report',
  '/providers': 'Providers',
  '/providers/:id': 'Provider Detail',
  '/contacts': 'Contacts',
  '/contacts/:id': 'Contact Detail',
  '/locations': 'Locations',
  '/locations/:id': 'Location Detail',
  '/organizations': 'Organizations',
  '/organizations/:id': 'Organization Detail',
  '/labels': 'Labels',
  '/labels/:id': 'Label Detail',
  '/label_types': 'Label Types',
  '/testing': 'Testing',
  '/testing/locations': 'Testing Locations',
  '/testing/labels': 'Testing Labels',
  '/visits': 'Visits & Referrals',
  '/documents': 'Documents',
  '/users': 'Users',
  '/users/:id': 'User Detail',
  '/clients': 'Clients',
  '/activity': 'Activity Log',
  '/usage': 'Usage Analytics',
  '/upload': 'Upload',
  '/taxonomy': 'Specialties',
  '/procedures': 'Procedures',
  '/rvu_benchmarks': 'RVU Benchmarks',
  '/login_settings': 'Login Settings',
};

export function normalizePath(pathname) {
  const clean = String(pathname || '/').split('?')[0].split('#')[0];
  const parts = clean.split('/').filter(Boolean).map((p) => (/^\d+$/.test(p) ? ':id' : p));
  return `/${parts.join('/')}`.replace(/\/$/, '') || '/';
}

export function labelForPath(pattern) {
  if (LABELS[pattern]) return LABELS[pattern];
  const seg = pattern.split('/').filter(Boolean)[0];
  if (!seg) return 'Home';
  return seg.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// --- State ---------------------------------------------------------------
let current = null; // { path, label, start } ; start === null while paused
let queue = [];
let ctxUserId = null;
let ctxClientId = null;
let inited = false;

const MAX_PAGE_MS = 10 * 60 * 1000; // idle cap: never count a single page as > 10 min
const FLUSH_INTERVAL_MS = 12000;
const MAX_QUEUE = 50;

// Finalize the current page's elapsed time into the queue (idle-capped).
function enqueueCurrent() {
  if (!current || current.start == null) return;
  const duration = Math.min(Date.now() - current.start, MAX_PAGE_MS);
  queue.push({
    path: current.path,
    full_path: current.full_path,
    duration_ms: duration,
  });
  if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
}

function send(events, keepalive) {
  if (!ctxUserId || !events.length) return;
  try {
    // Strip any trailing slash so we don't produce '/api//PageViews' (a double
    // slash makes the server parse an empty model and 500s the request).
    const base = (process.env.API_BASE_URL || '').replace(/\/+$/, '');
    const token = localStorage.getItem('auth');
    const url = `${base}/PageViews${ctxClientId ? `?selected_client_id=${ctxClientId}` : ''}`;
    fetch(url, {
      method: 'POST',
      keepalive: !!keepalive,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token || 'anonymous'}`,
      },
      body: JSON.stringify({ events }),
    }).catch(() => {});
  } catch (e) {
    /* never let telemetry throw */
  }
}

function flush(keepalive) {
  if (!queue.length) return;
  const batch = queue;
  queue = [];
  send(batch, keepalive);
}

// --- Public API ----------------------------------------------------------
export function initUsageBeacon() {
  if (inited || typeof window === 'undefined') return;
  inited = true;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      // Pause the clock so backgrounded time isn't counted, and flush now.
      enqueueCurrent();
      if (current) current.start = null;
      flush(true);
    } else if (current) {
      current.start = Date.now(); // resume
    }
  });

  window.addEventListener('pagehide', () => {
    enqueueCurrent();
    flush(true);
  });

  setInterval(() => flush(false), FLUSH_INTERVAL_MS);
}

export function setUsageContext(userId, clientId) {
  const newUser = userId || null;
  // If the logged-in user changed (login / logout / account switch in the same
  // tab), drop any pending events and the in-progress page so the previous
  // user's page views can never be attributed to the new user. Events are sent
  // with the current auth token, so anything still queued would otherwise flush
  // under the wrong user.
  if (newUser !== ctxUserId) {
    queue = [];
    current = null;
  }
  ctxUserId = newUser;
  ctxClientId = clientId || null;
}

export function trackPageView(pathname) {
  enqueueCurrent(); // finalize the page we're leaving (queued, not sent yet)
  const fullPath = String(pathname || '/').split('?')[0].split('#')[0]; // exact page, ids kept
  const path = normalizePath(pathname); // normalized pattern (type of page)
  current = { path, full_path: fullPath, start: Date.now() };
}
